import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, MessageSquare, Mail, User } from 'lucide-react';

interface RequestPasswordHelpProps {
  email?: string;
  onSuccess?: () => void;
}

export function RequestPasswordHelp({ email: initialEmail = '', onSuccess }: RequestPasswordHelpProps) {
  const [email, setEmail] = useState(initialEmail);
  const [playerName, setPlayerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (!playerName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if user exists with this email
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email.trim())
        .maybeSingle();

      // Insert password reset request (no SELECT needed for anonymous users)
      const { error } = await supabase
        .from('password_reset_requests')
        .insert({
          email: email.trim(),
          player_name: playerName.trim(),
          user_id: profileData?.id || null,
          status: 'pending'
        });

      if (error) {
        throw error;
      }

      // Send email notification to admin (fire and forget)
      try {
        await supabase.functions.invoke('notify-admin-password-request', {
          body: {
            playerName: playerName.trim(),
            email: email.trim()
          }
        });
        console.log('Admin notification email sent');
      } catch (emailError) {
        console.error('Failed to send admin notification:', emailError);
        // Don't fail the request if email fails
      }

      setIsSubmitted(true);
      toast.success('রিকোয়েস্ট পাঠানো হয়েছে! এডমিন শীঘ্রই যোগাযোগ করবেন।');
      onSuccess?.();

    } catch (error: any) {
      console.error('Error submitting password help request:', error);
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-4">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
          <MessageSquare className="h-6 w-6 text-green-500" />
        </div>
        <p className="text-sm text-muted-foreground">
          Request sent! Admin will reset your password and contact you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
          <User className="h-3 w-3" />
          Your Name
        </Label>
        <Input
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          placeholder="Enter your name"
          className="h-9 text-sm bg-muted/50"
        />
      </div>
      
      <div>
        <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
          <Mail className="h-3 w-3" />
          Your Email
        </Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="h-9 text-sm bg-muted/50"
        />
      </div>

      <Button 
        type="submit" 
        size="sm" 
        className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white border-0 shadow-lg shadow-emerald-500/20"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Sending Request...
          </>
        ) : (
          <>
            <MessageSquare className="h-4 w-4 mr-2" />
            Submit Request
          </>
        )}
      </Button>
    </form>
  );
}
